import { useMemo } from "react";
import { BLANK_FILTER_VALUE, toFilterOptionKey, toFilterOptionLabel } from "./filterFns";
import { ReactTable2Column, ReactTable2Table } from "./features";

export interface FilterOption {
    value: string;
    label: string;
}

/**
 * Builds the list of selectable options for a column's multi-select/select filter from its faceted
 * unique values.
 *
 *  - Empty/null/undefined cell values collapse into a single "(Blank)" option, sorted to the top.
 *  - getFacetedUniqueValues() reflects rows matching every *other* column's filter, giving Excel-style
 *    cascading options. We therefore recompute whenever any column filter changes, not just when this
 *    column instance changes (TanStack recreates column objects on most state updates).
 */
export const useColumnFilterOptions = (
    column: ReactTable2Column,
    table?: ReactTable2Table,
    options?: { flattenArrays?: boolean }
): FilterOption[] => {
    const columnFilters = table?.state?.columnFilters ?? [];
    const flattenArrays = options?.flattenArrays ?? false;

    return useMemo(() => {
        const keys = new Set<string>();
        Array.from(column.getFacetedUniqueValues().keys()).forEach((value) => {
            if (flattenArrays && Array.isArray(value)) {
                if (value.length === 0) {
                    keys.add(BLANK_FILTER_VALUE);
                } else {
                    value.forEach((item) => keys.add(toFilterOptionKey(item)));
                }
                return;
            }
            keys.add(toFilterOptionKey(value));
        });
        return Array.from(keys)
            .map((key) => ({ value: key, label: toFilterOptionLabel(key) }))
            .sort((a, b) => {
                if (a.value === BLANK_FILTER_VALUE) {
                    return -1;
                }
                if (b.value === BLANK_FILTER_VALUE) {
                    return 1;
                }
                return a.label.localeCompare(b.label);
            });
        // columnFilters is an intentional dependency: it drives the Excel-style cascading recompute.
    }, [column, columnFilters, flattenArrays]);
};
